import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, ArrowRight, Zap, AlertCircle, FileText } from "lucide-react";
import F24Modal from "@/components/F24Modal";

/**
 * NextDeadlineHero — card in evidenza sulla dashboard con la prossima scadenza attiva.
 * Include countdown in tempo reale (giorni · ore · minuti) e CTA "Preparati ora" che
 * apre la pagina più utile in base alla categoria (Calcolatori con tab pre-selezionata,
 * Verifica Fiscale, o Guide).
 *
 * Props:
 *   - deadlines: list of {id, title, description, due_date (ISO), category, completed}
 */

// Mappa categoria scadenza → azione consigliata (route + label + urgenza colore)
const ACTION_MAP = {
  IRPEF: { to: "/app/calcolatori?tab=irpef", label: "Calcola IRPEF" },
  IVA: { to: "/app/calcolatori?tab=iva", label: "Calcola IVA" },
  Forfettario: { to: "/app/calcolatori?tab=forfettario", label: "Calcola Forfettario" },
  ISEE: { to: "/app/calcolatori?tab=isee", label: "Calcola ISEE" },
  Bollo: { to: "/app/calcolatori?tab=bollo", label: "Calcola Bollo" },
  INPS: { to: "/app/verifica", label: "Vai alla Verifica" },
  F24: { to: "/app/verifica", label: "Prepara F24" },
  IMU: { to: "/app/verifica", label: "Vai alla Verifica" },
  "730": { to: "/app/guide", label: "Guida al 730" },
  Redditi: { to: "/app/verifica", label: "Prepara Redditi" },
  CU: { to: "/app/guide", label: "Guida CU" },
};

function resolveAction(category) {
  if (!category) return { to: "/app/scadenze", label: "Apri Scadenze" };
  // Normalizza (case-insensitive, strip)
  const key = String(category).trim();
  return ACTION_MAP[key] || { to: "/app/scadenze", label: "Apri Scadenze" };
}

function computeCountdown(dueDateISO) {
  if (!dueDateISO) return null;
  // Confrontiamo sulla mezzanotte locale per stabilità (le scadenze fiscali non hanno orario)
  const now = new Date();
  const target = new Date(`${dueDateISO}T23:59:59`);
  const diffMs = target.getTime() - now.getTime();
  if (diffMs <= 0) return { days: 0, hours: 0, minutes: 0, past: true };
  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / 60 / 24);
  const hours = Math.floor((totalMinutes / 60) % 24);
  const minutes = totalMinutes % 60;
  return { days, hours, minutes, past: false };
}

// Categorie per cui l'F24 pre-compilato è utile
const F24_CATEGORIES = new Set(["IRPEF", "IVA", "INPS", "IMU", "F24", "Redditi", "Forfettario"]);

export default function NextDeadlineHero({ deadlines = [] }) {
  const [f24Open, setF24Open] = useState(false);
  // Trova la scadenza attiva più vicina (future + not completed)
  const next = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return [...deadlines]
      .filter((d) => !d.completed && d.due_date && d.due_date >= today)
      .sort((a, b) => a.due_date.localeCompare(b.due_date))[0];
  }, [deadlines]);

  const [countdown, setCountdown] = useState(() => computeCountdown(next?.due_date));

  useEffect(() => {
    if (!next) return;
    setCountdown(computeCountdown(next.due_date));
    const t = setInterval(() => setCountdown(computeCountdown(next.due_date)), 60000);
    return () => clearInterval(t);
  }, [next?.due_date]);

  if (!next) return null; // nessuna scadenza attiva → il componente si nasconde

  const action = resolveAction(next.category);
  const canF24 = F24_CATEGORIES.has(next.category);
  const isUrgent = countdown && countdown.days <= 7;
  const isCritical = countdown && countdown.days <= 1;

  const cardBg = isCritical
    ? "from-secondary/20 to-secondary/5 border-secondary/40"
    : isUrgent
    ? "from-primary/10 to-primary/5 border-primary/40"
    : "from-accent/50 to-transparent border-primary/10";

  const chipBg = isCritical ? "bg-secondary text-secondary-foreground" : "bg-primary text-primary-foreground";
  const iconBg = isCritical ? "bg-secondary" : "bg-primary";
  const Icon = isCritical ? AlertCircle : isUrgent ? Zap : Clock;

  return (
    <Card
      className={`p-6 lg:p-8 rounded-2xl border-2 bg-gradient-to-br ${cardBg} fade-in-up`}
      data-testid="dashboard-next-deadline-hero"
    >
      <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
        {/* Icona + timer */}
        <div className="flex items-start gap-3 md:min-w-[280px]">
          <div className={`w-12 h-12 md:w-14 md:h-14 rounded-2xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
            <Icon className="w-6 h-6 md:w-7 md:h-7 text-primary-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
              {isCritical ? "Ultimo giorno" : isUrgent ? "Manca poco" : "Prossima scadenza"}
            </div>
            <div className="flex items-baseline gap-2 mt-1 flex-wrap" data-testid="next-deadline-countdown">
              <span className="font-heading font-black text-3xl md:text-4xl lg:text-5xl tracking-tighter text-foreground tabular-nums">
                {countdown?.days ?? 0}
              </span>
              <span className="text-base md:text-lg font-heading text-muted-foreground">
                {countdown?.days === 1 ? "giorno" : "giorni"}
              </span>
              <span className="text-xs md:text-sm text-muted-foreground tabular-nums hidden sm:inline">
                · {String(countdown?.hours ?? 0).padStart(2, "0")}h {String(countdown?.minutes ?? 0).padStart(2, "0")}m
              </span>
            </div>
            <div className="text-[11px] md:text-xs text-muted-foreground mt-1">
              Scade il {new Date(next.due_date).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
            </div>
          </div>
        </div>

        {/* Info scadenza */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${chipBg}`}>
              {next.category || "Scadenza"}
            </span>
          </div>
          <div className="font-heading font-bold text-lg md:text-xl lg:text-2xl leading-tight" data-testid="next-deadline-title">
            {next.title}
          </div>
          {next.description && (
            <div className="text-xs md:text-sm text-muted-foreground mt-1 line-clamp-2">{next.description}</div>
          )}
        </div>

        {/* CTA — full width su mobile, colonna su desktop */}
        <div className="flex flex-col sm:flex-row md:flex-col gap-2 md:min-w-[200px]">
          {canF24 && (
            <Button
              onClick={() => setF24Open(true)}
              className="rounded-full h-11 w-full shadow-md"
              data-testid="next-deadline-cta-f24"
            >
              <FileText className="w-4 h-4 mr-1.5" /> Prepara F24
            </Button>
          )}
          <Link to={action.to} data-testid="next-deadline-cta-prepara" className="w-full">
            <Button variant={canF24 ? "outline" : "default"} className="rounded-full h-11 w-full shadow-md">
              {action.label} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
          <Link to="/app/scadenze" data-testid="next-deadline-cta-tutte" className="w-full">
            <Button variant="ghost" className="rounded-full h-11 w-full">
              Vedi tutte
            </Button>
          </Link>
        </div>
      </div>
      <F24Modal open={f24Open} onOpenChange={setF24Open} deadline={next} />
    </Card>
  );
}
